import { withAlpha } from '@/plugins/highcharts/chartColors';
import { i18n } from '@/plugins/internationalization/i18n';
import type { ProductivityComparisonBucket, ProductivityProductionGroup } from '@/types/SectionLiveKpi';
import type { ProductionGroup } from '@/types/ProductionGroup';
import type { ProductivityGranularity } from '@/plugins/store/actions/queries/active-groups';
import { DAY_MS } from './productivityFormatting';
import { METRIC_KEYS, metricField, metricKeysInBand, PRODUCTIVITY_AXIS, PRODUCTIVITY_METRICS, type MetricFlags, type MetricKey } from './productivityMetrics';
import { metricColumnRange, productivityPlottableValue } from './productivitySeries';

// One Production Group the comparison dropdown can offer, alongside why it is offered - carried
// through to the picker for its empty/disabled copy, though the two reasons are never both true
// for the same candidate (a favourite still standing in as the preceding period would collapse to
// one entry - see resolveComparisonCandidates).
export type ComparisonCandidate = {
    productionGroupId: number;
    name: string;
    isFavorite: boolean;
};

// The picked reference as a candidate, resolved against the Section's whole history rather than
// against what the dropdown currently offers. The two differ exactly while the range covers several
// Production Groups: the dropdown offers nothing then (the periods are already on the axis in their own
// dates), but the pick itself is still standing and is still in the URL, so something on screen has
// to be able to name it. Null once the id names no Production Group this Section ever had - a different
// Section, or a reference since deleted.
export function resolveSelectedComparison(history: ProductionGroup[], selectedProductionGroupId: number | null): ComparisonCandidate | null {
    if (selectedProductionGroupId === null) return null;
    const productionGroup = history.find((candidate) => candidate.id === selectedProductionGroupId);
    return productionGroup === undefined ? null : { productionGroupId: productionGroup.id, name: productionGroup.name, isFavorite: productionGroup.is_favorite };
}

// The comparison dropdown's contents: the Production Group immediately preceding the one currently on
// the chart, plus every favourited Production Group - both drawn from the Section's own history, which
// the caller already has loaded for the barn (no request of its own). Returns nothing while the
// chart has no single Production Group in view (`currentProductionGroup` null - either no Production Group or the range
// spans several, which the control being unavailable already covers) or while the caller has no
// history to search yet (favourites should never appear ahead of the barn's own Production Group load).
export function resolveComparisonCandidates(history: ProductionGroup[], currentProductionGroup: ProductivityProductionGroup | null): ComparisonCandidate[] {
    if (!currentProductionGroup) return [];

    const others = history.filter((productionGroup) => productionGroup.id !== currentProductionGroup.production_group_id);

    // The most recently closed Production Group strictly before the current one's own start - "the
    // period immediately preceding this Section's current one", not merely any earlier period.
    const preceding = others
        .filter((productionGroup) => productionGroup.end !== null && productionGroup.end < currentProductionGroup.start)
        .sort((a, b) => b.end.localeCompare(a.end))[0];

    const candidatesById = new Map<number, ProductionGroup>();
    if (preceding) candidatesById.set(preceding.id, preceding);
    for (const productionGroup of others.filter((sp) => sp.is_favorite)) candidatesById.set(productionGroup.id, productionGroup);

    return [...candidatesById.values()].map((productionGroup) => ({
        productionGroupId: productionGroup.id,
        name: productionGroup.name,
        isFavorite: productionGroup.is_favorite,
    }));
}

// Re-projects a comparison bucket's day-in-period onto an absolute instant on the currently
// displayed Production Group's own timeline: day 1 lands exactly on `currentProductionGroupStart`, day 2 the
// day after, and so on - regardless of how the compared Production Group's own calendar dates fell.
// This is what makes day 34 of a reference read against day 34 of the period on screen.
export function projectComparisonTimestamp(dayInPeriod: number, currentProductionGroupStartIso: string): number {
    return Date.parse(currentProductionGroupStartIso) + (dayInPeriod - 1) * DAY_MS;
}

// The instant just past the reference's last projected day, i.e. where the chart's "past today"
// shading ends. Measured from the *displayed* Production Group's start, since that is where
// projectComparisonTimestamp lands day 1 - not from the compared Production Group's own calendar dates.
export function projectedComparisonEndMs(currentProductionGroupStartIso: string, lengthDays: number): number {
    return Date.parse(currentProductionGroupStartIso) + lengthDays * DAY_MS;
}

// The bucket fields a comparison read carries. Every field the primary chart draws a metric from
// except `water_litres`: the reference always states its water per pig per day, whatever
// granularity the chart itself is on.
const COMPARISON_BUCKET_FIELDS = ['weight_kg', 'daily_gain_g', 'growth_index', 'water_per_pig', 'norm_water_per_pig', 'dead_count'] as const;

export type ComparisonBucketField = (typeof COMPARISON_BUCKET_FIELDS)[number];

function isComparisonBucketField(field: string): field is ComparisonBucketField {
    return (COMPARISON_BUCKET_FIELDS as readonly string[]).includes(field);
}

/**
 * Which comparison-bucket field carries `key`'s reference figure at this granularity, or null when
 * the reference has none the chart could draw. A halo is only ever read against its primary series'
 * own axis, so it has to be the very field that series is plotting - not merely the same metric. At
 * Hour the water band is metered litres and the reference only knows litres per pig, which on a
 * 0-4000 L axis does not read as an absent series but as a measured flat line along zero.
 *
 * Answered by field identity rather than by a per-metric granularity rule, so the registry stays
 * the only place that says what a metric is drawn from and a metric whose unit changes with the
 * granularity needs no separate rule here.
 */
export function comparisonField(key: MetricKey, granularity: ProductivityGranularity): ComparisonBucketField | null {
    // The metric's one bucket field, kept only if the comparison read states that field too - the
    // halo carries fewer of them than the primary series does (no metered litres, for one).
    const field = metricField(key, granularity);
    return isComparisonBucketField(field) ? field : null;
}

// A halo shares its primary series' axis, so a non-physical reference point scales that axis exactly
// as a non-physical primary point would - which is what the primary series has been filtered for all
// along. Blanked through the same per-metric gate, so the halo draws a gap where the reference has a
// restock artefact rather than a spike that squashes both curves into a sliver.
function buildComparisonBucketSeriesData(
    buckets: ProductivityComparisonBucket[],
    currentProductionGroupStartIso: string,
    key: MetricKey,
    valueKey: ComparisonBucketField
): Array<[number, number | null]> {
    return buckets
        .map((bucket): [number, number | null] => [
            projectComparisonTimestamp(bucket.day_in_period, currentProductionGroupStartIso),
            productivityPlottableValue(key, bucket[valueKey]),
        ])
        .sort((a, b) => a[0] - b[0]);
}

// Same-hue halo colours, one per metric its own primary series also uses (see
// ProductivityChart.vue) - the halo can only read as "same metric, earlier" if it shares
// its primary series' hue, which an auto-palette colour can't guarantee.
const HALO_OPACITY = 0.32;
const HALO_LINE_WIDTH = 6;

/**
 * Whether this metric's reference is drawn as bins beside the actual rather than as a silhouette
 * behind it. A count is compared bin to bin: a line through daily dead counts joins single-day
 * events into ridges that never happened, and reads as a level the Section sat at rather than as the
 * days something occurred.
 *
 * True where the metric is drawn as a column and is the only metric in its band, which is what
 * dodging needs - the pair splits the band's whole width between them. Water is a column too, but
 * its band already stacks the norm behind the actual with the over-norm highlight on top, and a
 * fourth bar dodged into that is unreadable; it keeps the silhouette.
 */
export function comparedAsBins(key: MetricKey): boolean {
    return PRODUCTIVITY_METRICS[key].shape === 'column' && metricKeysInBand(PRODUCTIVITY_METRICS[key].band).length === 1;
}

/**
 * The reference as bins beside the actual: same day, same width, the metric's own hue at the halo's
 * own opacity, so the pair is read by comparing two heights. Grouping is left on (unlike the water
 * columns, which deliberately overlap) - that is what makes Highcharts dodge the two rather than
 * draw one over the other.
 */
function buildHaloBinSeries(
    id: string,
    name: string,
    color: string,
    yAxisIndex: number,
    visible: boolean,
    data: Array<[number, number | null]>,
    pointRange: number
) {
    return {
        id,
        type: 'column' as const,
        name,
        yAxis: yAxisIndex,
        data,
        visible,
        color: withAlpha(color, HALO_OPACITY),
        pointRange,
        borderRadius: 1,
        enableMouseTracking: false,
        showInLegend: false,
        zIndex: 0,
        shadow: false,
    };
}

// One metric's reference halo: a same-hue, low-opacity, wider-stroke line - never a dashed grey
// line- drawn behind its primary series (lower zIndex) so it reads
// as "same metric, earlier" rather than competing with the actual measurement on top of it. Shared
// by every metric's halo, including the two water series and deadCount's column, since the point
// is a same-shape silhouette to compare against, not a pixel-exact re-rendering of each metric's
// own chart type.
function buildHaloSeries(id: string, name: string, color: string, yAxisIndex: number, visible: boolean, data: Array<[number, number | null]>) {
    return {
        id,
        type: 'line' as const,
        name,
        yAxis: yAxisIndex,
        data,
        visible,
        color: withAlpha(color, HALO_OPACITY),
        lineWidth: HALO_LINE_WIDTH,
        dashStyle: 'Solid' as const,
        marker: { enabled: false },
        enableMouseTracking: false,
        showInLegend: false,
        zIndex: 0,
        // Highcharts fills any Series option position not set above from the primary series'
        // shared chart-level style options, but a wide translucent halo never needs shadowing.
        shadow: false,
    };
}

// Every halo series the currently loaded comparison can draw, one per metric, positioned on the
// same yAxis as its primary counterpart (PRODUCTIVITY_AXIS) so a halo overlays exactly the axis its primary
// series is read against. Driven by the metric registry rather than by six hand-written blocks: the
// axis, the colour, the label and the field a halo needs are the same facts its primary series is
// already declared with, and restating them here is how the two drift apart.
//
// A metric the reference cannot express against the current axis is absent entirely rather than
// hidden (see comparisonField) - `visible: false` would still leave Highcharts a series to scale
// the axis to. Visibility otherwise mirrors the primary bands' own toggles exactly: picking a
// reference redraws it across every currently visible metric at once, and a metric the reader has
// hidden stays hidden in its halo form too.
export function buildComparisonHaloSeries(
    referenceName: string,
    buckets: ProductivityComparisonBucket[],
    currentProductionGroupStartIso: string,
    visibility: MetricFlags,
    granularity: ProductivityGranularity
) {
    const haloName = (metricName: string) => i18n.global.t('views.monitoring.activeGroups.modals.productivity.compareReferenceSeriesName', { name: referenceName, metric: metricName }) as string;

    return METRIC_KEYS.flatMap((key) => {
        const field = comparisonField(key, granularity);
        if (field === null) return [];

        const metric = PRODUCTIVITY_METRICS[key];
        const id = `${metric.seriesId}Compare`;
        const name = haloName(i18n.global.t(metric.labelKey) as string);
        const data = buildComparisonBucketSeriesData(buckets, currentProductionGroupStartIso, key, field);
        return comparedAsBins(key)
            ? buildHaloBinSeries(id, name, metric.color, PRODUCTIVITY_AXIS[metric.axis], visibility[key], data, metricColumnRange(key, granularity))
            : buildHaloSeries(id, name, metric.color, PRODUCTIVITY_AXIS[metric.axis], visibility[key], data);
    });
}
